import { createContext, useContext, useMemo, type CSSProperties, type ReactNode } from 'react'

export interface ThemeConfig {
  /** Brand primary color */
  primary?: string
  /** Brand primary hover color */
  primaryHover?: string
  /** Brand primary light color */
  primaryLight?: string
  /** Brand secondary color */
  secondary?: string
  /** Brand secondary hover color */
  secondaryHover?: string
  /** Brand accent color */
  accent?: string
  /** Brand font family (body text) */
  fontSans?: string
  /** Brand display font family (headings) */
  fontDisplay?: string
  /** Navy heading color */
  navy?: string
  /** Background color */
  background?: string
  /** Surface color */
  surface?: string
  /** Border color */
  border?: string
  /** Text color */
  text?: string
  /** Secondary text color */
  textSecondary?: string
  /** Border radius scale */
  radiusSm?: string
  radiusMd?: string
  radiusLg?: string
  /** Additional custom CSS variables (key should include the -- prefix) */
  custom?: Record<string, string>
}

export interface ThemeProviderProps {
  children: ReactNode
  theme?: ThemeConfig
}

const ThemeContext = createContext<ThemeConfig | undefined>(undefined)

/**
 * Access the current theme configuration.
 * Returns undefined if no theme overrides are active.
 */
export function useTheme(): ThemeConfig | undefined {
  return useContext(ThemeContext)
}

const TOKEN_MAP: Record<string, string> = {
  primary: '--color-primary',
  primaryHover: '--color-primary-hover',
  primaryLight: '--color-primary-light',
  secondary: '--color-secondary',
  secondaryHover: '--color-secondary-hover',
  accent: '--color-accent',
  fontSans: '--font-sans',
  fontDisplay: '--font-display',
  navy: '--color-navy',
  background: '--color-background',
  surface: '--color-surface',
  border: '--color-border',
  text: '--color-text',
  textSecondary: '--color-text-secondary',
  radiusSm: '--radius-sm',
  radiusMd: '--radius-md',
  radiusLg: '--radius-lg',
}

/**
 * ThemeProvider applies tenant-specific CSS custom property overrides.
 * Wraps the app and injects override styles on a wrapper div.
 */
export function ThemeProvider({ children, theme }: ThemeProviderProps) {
  const cssVars = useMemo(() => {
    if (!theme) return undefined

    const vars: Record<string, string> = {}

    for (const [key, cssVar] of Object.entries(TOKEN_MAP)) {
      const value = theme[key as keyof ThemeConfig]
      if (typeof value === 'string') {
        vars[cssVar] = value
      }
    }

    if (theme.custom) {
      for (const [key, value] of Object.entries(theme.custom)) {
        vars[key] = value
      }
    }

    return Object.keys(vars).length > 0 ? vars : undefined
  }, [theme])

  return (
    <ThemeContext.Provider value={theme}>
      {cssVars ? (
        <div style={cssVars as CSSProperties}>{children}</div>
      ) : (
        children
      )}
    </ThemeContext.Provider>
  )
}
